/**
 * useSandboxLifecycle
 *
 * A unified hook that manages the complete sandbox lifecycle state machine:
 *   none → provisioning → active → stopping → none
 *
 * This eliminates code duplication across:
 *   - ProjectDetailsPage (Student Course Project sandbox)
 *   - PracticeProjectDetailsPage (Student Practice Project sandbox)
 *   - EnhancedSubmissionDetailsPage (Instructor Review sandbox)
 *
 * Usage:
 *   const lifecycle = useSandboxLifecycle({
 *     sandboxId: project.id,
 *     launchFn: () => createWorkspaceMutation.mutateAsync({ projectId: project.id }),
 *     onActive: (data) => updateWorkspace({ ... }),
 *     onIdle: () => resetWorkspace(),
 *   });
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  useWorkspaceStatusQuery,
  useCancelProvisioningMutation,
  useStopSandboxMutation,
  WorkspaceStatusResponse,
} from "../api/jupyterManagementApi";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SandboxStatus {
  id?: string;
  status: "none" | "provisioning" | "active" | "stopping" | "stopped" | "error";
  instanceId?: string;
  provisioningProgress?: number;
  estimatedTime?: string;
  createdAt?: string;
  lastActivity?: string;
  errorMessage?: string;
  autoSaveEnabled?: boolean;
}

export interface SandboxLifecycleConfig {
  /** The ID used as the polling key (projectId, submissionId, practice projectId, etc.) */
  sandboxId: string;

  /**
   * The async function to call when launching the sandbox.
   * Must return { isSuccess: boolean; message?: string }.
   * Each page injects its own backend call (createWorkspace vs launchReviewSandbox).
   */
  launchFn: () => Promise<{ isSuccess: boolean; message?: string }>;

  /**
   * Optional: override the default stop behaviour.
   * If not provided, the hook calls useStopSandboxMutation with `{ sessionId, projectId }`.
   */
  stopFn?: (params: { sessionId?: string; projectId?: string; username?: string }) => Promise<void>;

  /**
   * Called when polling confirms the workspace is `active`.
   * Use this to write to JupyterWorkspaceContext.
   */
  onActive?: (data: WorkspaceStatusResponse["data"]) => void;

  /**
   * Called when polling confirms the workspace has reached `idle`.
   * Use this to call `resetWorkspace()`.
   */
  onIdle?: () => void;

  /**
   * Called when provisioning begins (either via handleLaunch or detected via polling).
   * Use this to update the global JupyterWorkspaceContext status to 'provisioning'.
   */
  onProvisioning?: () => void;

  /**
   * The current session ID from the workspace context (for stop requests).
   */
  sessionId?: string | null;

  /**
   * The current user's identifier (for stop requests).
   */
  userIdentifier?: string;

  /**
   * Optional reason why launching is disabled (e.g. sandbox time exhausted).
   * When set, `handleLaunch` will show an error toast and abort.
   */
  launchDisabledReason?: string;

  /**
   * If true, skip the guard that prevents launching when already active/provisioning.
   * Not normally needed.
   */
  allowRelaunch?: boolean;

  /** Toast ID used for launch/cancel toasts (defaults to "sandbox-lifecycle") */
  toastId?: string;
}

export interface SandboxLifecycleReturn {
  sandboxStatus: SandboxStatus;
  setSandboxStatus: React.Dispatch<React.SetStateAction<SandboxStatus>>;
  handleLaunch: () => Promise<void>;
  handleCancel: () => Promise<void>;
  handleStop: () => Promise<void>;
  isSandboxTimeExhausted: boolean;
  setIsSandboxTimeExhausted: (v: boolean) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSandboxLifecycle(config: SandboxLifecycleConfig): SandboxLifecycleReturn {
  const {
    sandboxId,
    launchFn,
    stopFn,
    onActive,
    onIdle,
    onProvisioning,
    sessionId,
    userIdentifier,
    launchDisabledReason,
    toastId = config.toastId || `sandbox-${sandboxId}`,
  } = config;

  // ── Local state ─────────────────────────────────────────────────────────────
  const [sandboxStatus, setSandboxStatus] = useState<SandboxStatus>({ status: "none" });
  const [provisioningStartedAt, setProvisioningStartedAt] = useState<number | null>(null);
  const [lastCancelAt, setLastCancelAt] = useState<number | null>(null);
  const [isSandboxTimeExhausted, setIsSandboxTimeExhausted] = useState(false);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const cancelProvisioningMutation = useCancelProvisioningMutation();
  const stopSandboxMutation = useStopSandboxMutation();

  // ── Polling ──────────────────────────────────────────────────────────────────
  const pollingEnabled =
    sandboxStatus.status === "provisioning" ||
    sandboxStatus.status === "stopping" ||
    !!provisioningStartedAt ||
    !!lastCancelAt;

  const workspaceStatusQuery = useWorkspaceStatusQuery(sandboxId, pollingEnabled);

  // ── Polling effect ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!workspaceStatusQuery.data) return;
    const { status, progress, message, data, timestamp } = workspaceStatusQuery.data;

    // ── provisioning ──────────────────────────────────────────────────────────
    if (status === "provisioning") {
      // Hard guard: if we are already stopping, ignore all provisioning signals
      if (sandboxStatus.status === "stopping") return;

      // Soft guard: ignore provisioning for 10s after a cancel to prevent flickering
      const isRecentlyCancelled = lastCancelAt && Date.now() - lastCancelAt < 10_000;
      if (isRecentlyCancelled) return;

      if (sandboxStatus.status !== "provisioning") {
        setSandboxStatus({ status: "provisioning", provisioningProgress: progress || 10 });
        onProvisioning?.();
      } else if (progress && progress > (sandboxStatus.provisioningProgress || 0)) {
        // Only update progress bar forward, never backward
        setSandboxStatus((prev) => ({ ...prev, provisioningProgress: progress }));
      }
      return;
    }

    // ── active ────────────────────────────────────────────────────────────────
    if (status === "active" && data) {
      // Hard guard: if we are stopping, ignore active signals entirely
      if (sandboxStatus.status === "stopping") return;

      // Only the provisioning → active handshake may flip this hook. A stale TanStack
      // cache of "active" must not resurrect the panel after the Redis lease was cleared
      // (idle timeout / reconcile) while sandboxStatus has returned to "none".
      if (sandboxStatus.status !== "provisioning") return;

      toast.success("Sandbox is ready!", { id: toastId });

      setSandboxStatus({
        status: "active",
        instanceId: data.server?.state?.object_name,
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        autoSaveEnabled: true,
        provisioningProgress: 100,
      });

      // Clear timing guards
      setProvisioningStartedAt(null);
      setLastCancelAt(null);

      // Notify the page so it can update workspace context
      onActive?.(data);
      return;
    }

    // ── idle ──────────────────────────────────────────────────────────────────
    if (status === "idle") {
      // Guard: ignore idle for the first 60s after launch to prevent race conditions
      const isRecentlyStarted = provisioningStartedAt && Date.now() - provisioningStartedAt < 60_000;
      if (isRecentlyStarted) return;

      if (sandboxStatus.status !== "none") {
        setSandboxStatus({ status: "none" });
        toast.dismiss();
        onIdle?.();
      }

      // Clear timing guards once fully idle
      setProvisioningStartedAt(null);
      setLastCancelAt(null);
      return;
    }

    // ── error ─────────────────────────────────────────────────────────────────
    if (status === "error") {
      // Stale-error filter: ignore errors that predate our current launch attempt
      const isStaleError =
        timestamp && provisioningStartedAt && timestamp < provisioningStartedAt;
      const isGracePeriod =
        !timestamp && provisioningStartedAt && Date.now() - provisioningStartedAt < 2_000;

      if (!isStaleError && !isGracePeriod) {
        setSandboxStatus({
          status: "error",
          errorMessage: message || "Failed to create Jupyter workspace.",
        });
        toast.error(message || "Failed to create Jupyter workspace.", { id: toastId });
      }
    }
  }, [
    workspaceStatusQuery.data,
    workspaceStatusQuery.dataUpdatedAt,
    sandboxStatus.status,
    sandboxId,
    provisioningStartedAt,
    lastCancelAt,
    toastId,
    onActive,
    onIdle,
    onProvisioning,
  ]);

  // ── handleLaunch ─────────────────────────────────────────────────────────────
  const handleLaunch = useCallback(async () => {
    if (sandboxStatus.status === "provisioning" || sandboxStatus.status === "active") {
      toast.error("A sandbox environment is already running or provisioning.", { id: toastId });
      return;
    }

    if (launchDisabledReason) {
      toast.error(launchDisabledReason, { id: toastId });
      return;
    }

    if (!sandboxId) {
      toast.error("Cannot launch sandbox: sandbox ID is missing.", { id: toastId });
      return;
    }

    setSandboxStatus({ status: "provisioning", provisioningProgress: 10 });
    setProvisioningStartedAt(Date.now());
    setLastCancelAt(null);
    toast.loading("Starting sandbox provisioning...", { id: toastId });

    try {
      const response = await launchFn();
      if (!response.isSuccess) {
        throw new Error(response.message || "Failed to initiate sandbox provisioning.");
      }
      onProvisioning?.();
      // Success: polling effect will handle the rest
    } catch (error) {
      let errorMessage = "An unknown error occurred.";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.data?.message || error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      if (errorMessage.toLowerCase().includes("exhausted your sandbox time")) {
        setIsSandboxTimeExhausted(true);
      }

      toast.error(`Sandbox launch failed: ${errorMessage}`, { id: toastId });

      // Guard: if we already transitioned to stopping (e.g. cancel mid-launch), don't overwrite
      setSandboxStatus((prev) =>
        prev.status === "stopping" || !!lastCancelAt
          ? prev
          : { status: "error", errorMessage }
      );
    }
  }, [sandboxStatus.status, launchDisabledReason, sandboxId, launchFn, toastId, onProvisioning, lastCancelAt]);

  // ── handleCancel ──────────────────────────────────────────────────────────────
  const handleCancel = useCallback(async () => {
    if (!sandboxId) return;

    // Immediately move to stopping — polling effect will wait for backend idle
    setSandboxStatus({ status: "stopping" });
    setProvisioningStartedAt(null);
    setLastCancelAt(Date.now());

    toast.loading("Cancelling sandbox launch...", { id: toastId });

    try {
      await cancelProvisioningMutation.mutateAsync(sandboxId);
      toast.success("Cancellation request sent.", { id: toastId });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to cancel sandbox. Please refresh.",
        { id: toastId }
      );
      console.error("[useSandboxLifecycle] Cancel error:", error);
    }
  }, [sandboxId, toastId, cancelProvisioningMutation]);

  // ── handleStop ────────────────────────────────────────────────────────────────
  const handleStop = useCallback(async () => {
    // Immediately move to stopping — polling effect will wait for backend idle
    setSandboxStatus((prev) => ({ ...prev, status: "stopping" }));
    toast.loading("Stopping sandbox environment...", { id: toastId });

    try {
      if (stopFn) {
        await stopFn({ sessionId: sessionId ?? undefined, projectId: sandboxId, username: userIdentifier });
      } else {
        await stopSandboxMutation.mutateAsync({
          sessionId: sessionId ?? undefined,
          projectId: sandboxId,
          username: userIdentifier,
        });
      }

      // Mark that we sent a stop request — polling will transition to none when backend is idle
      setLastCancelAt(Date.now());
      toast.success("Stop request sent.", { id: toastId });
    } catch (error) {
      let errorMessage = "Failed to stop the sandbox.";
      if (axios.isAxiosError(error)) {
        errorMessage =
          error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { id: toastId });

      // Revert to active if stop failed
      setSandboxStatus((prev) => ({ ...prev, status: "active" }));
    }
  }, [sandboxId, sessionId, userIdentifier, stopFn, toastId, stopSandboxMutation]);

  return {
    sandboxStatus,
    setSandboxStatus,
    handleLaunch,
    handleCancel,
    handleStop,
    isSandboxTimeExhausted,
    setIsSandboxTimeExhausted,
  };
}
